import { Link } from 'react-router-dom';
import { Card, CabecalhoDePainel, TituloDePainel } from '../components/Painel';
import type { ContentState } from '@ferrari/types';
import { api, type ContentListResponse, type ConfigurationResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant, formatRelative } from '../lib/format';

/**
 * Quadro de workflow de conteúdo (Back Office §8).
 *
 * A tela **não infere transição**. Ela mostra o estado que o servidor gravou e o
 * motivo que veio com ele. Se a interface calculasse "o próximo passo é X", a
 * regra existiria em dois lugares — e a cópia da UI seria a que fica velha, do
 * jeito silencioso que o dono descobre com um botão que não faz nada.
 */

/**
 * Colunas do quadro: estágios, não os 30 estados.
 *
 * Trinta colunas seriam ilegíveis. O agrupamento é por ESTÁGIO da esteira, e
 * cada cartão mostra o estado exato — assim nada se perde no resumo.
 *
 * Os nomes de estado vêm do catálogo compartilhado (`ContentState`), e um teste
 * varre o catálogo inteiro exigindo que cada estado apareça em exatamente um
 * estágio e tenha rótulo. Escrever os nomes de cabeça foi o que colocou aqui,
 * na primeira versão, estados que não existem em lugar nenhum: o quadro parecia
 * certo e teria jogado TODAS as peças na coluna de exceção.
 */
export const STAGES: readonly { id: string; label: string; states: readonly ContentState[] }[] = [
  {
    id: 'editorial',
    label: 'Editorial',
    states: ['DISCOVERED', 'UNDER_EDITORIAL_REVIEW', 'SELECTED', 'EVERGREEN', 'CREATIVE_BRIEF'],
  },
  {
    id: 'criacao',
    label: 'Criação',
    states: ['SCRIPTING', 'REVISION'],
  },
  {
    id: 'governanca',
    label: 'Governança',
    states: ['PREPRODUCTION_GOVERNANCE', 'SEMANTICALLY_APPROVED'],
  },
  {
    id: 'producao',
    label: 'Produção',
    states: ['WAITING_PRODUCTION', 'COST_CHECK', 'PRODUCING', 'TECHNICAL_QA'],
  },
  {
    id: 'publicacao',
    label: 'Publicação',
    states: ['READY_TO_PUBLISH', 'RELEASE_CHECK', 'QUEUED', 'SCHEDULED', 'PUBLISHING', 'PUBLISHED'],
  },
  {
    id: 'aprendizado',
    label: 'Medição e aprendizado',
    states: ['MEASURING', 'LEARNED', 'REPURPOSE_REQUESTED', 'RETIRED'],
  },
  {
    id: 'parado',
    label: 'Parado',
    states: ['DEFERRED', 'REJECTED', 'BLOCKED', 'ESCALATED', 'HELD', 'CANCELLED', 'PUBLISH_FAILED'],
  },
];

/** Estado exato em português. A pessoa não lê nome de constante. */
export const STATE_LABEL: Record<ContentState, string> = {
  DISCOVERED: 'Descoberto',
  UNDER_EDITORIAL_REVIEW: 'Em triagem editorial',
  SELECTED: 'Selecionado',
  DEFERRED: 'Adiado',
  REJECTED: 'Recusado',
  EVERGREEN: 'Perene, sem prazo',
  CREATIVE_BRIEF: 'Com briefing criativo',
  SCRIPTING: 'Em roteiro',
  PREPRODUCTION_GOVERNANCE: 'Em governança (pré-produção)',
  REVISION: 'Em revisão',
  BLOCKED: 'Bloqueado',
  ESCALATED: 'Escalado',
  SEMANTICALLY_APPROVED: 'Aprovado semanticamente',
  WAITING_PRODUCTION: 'Aguardando produção',
  COST_CHECK: 'Em checagem de custo',
  PRODUCING: 'Em produção',
  TECHNICAL_QA: 'Em QA técnico',
  READY_TO_PUBLISH: 'Pronto para publicar',
  RELEASE_CHECK: 'Em checagem de release',
  QUEUED: 'Na fila',
  SCHEDULED: 'Agendado',
  PUBLISHING: 'Publicando',
  PUBLISHED: 'Publicado',
  PUBLISH_FAILED: 'Falhou ao publicar',
  HELD: 'Retido pelo dono',
  CANCELLED: 'Cancelado',
  MEASURING: 'Em medição',
  LEARNED: 'Aprendizado extraído',
  REPURPOSE_REQUESTED: 'Reaproveitamento pedido',
  RETIRED: 'Aposentado',
};

const NOW = () => new Date();

/**
 * O painel de volume diario que o mockup desenha na area de Publicacao.
 *
 * Ele e SOMENTE LEITURA aqui, e o motivo nao e preguica: mudar um teto passa
 * pelo fluxo de configuracao, que exige motivo escrito, versao e auditoria.
 * Um segundo formulario editando as mesmas quatro chaves seria a mesma regra em
 * dois caminhos, e a copia que envelhece e sempre a menos usada. O botao leva
 * para onde a mudanca acontece de verdade.
 *
 * Os valores saem da configuracao APROVADA. O desenho mostra 3/4/7/10, que e o
 * seed do documento; sem versao aprovada a tela diz "nao configurado" em vez de
 * anunciar como vigente um numero que ninguem aprovou.
 */
const VOLUME_LINHAS = [
  { chave: 'publishing.operational_floor_posts_per_day', rotulo: 'Piso operacional' },
  { chave: 'publishing.desired_min_posts_per_day', rotulo: 'Mínimo desejado' },
  { chave: 'publishing.desired_max_posts_per_day', rotulo: 'Máximo desejado' },
  { chave: 'publishing.absolute_auto_max_posts_per_day', rotulo: 'Máximo automático absoluto' },
] as const;

/** Como o sistema LE cada faixa. Texto do mockup, que e a regra em portugues. */
const FAIXAS = [
  { faixa: '0 a 2 posts', leitura: 'Somente quando o Quality Gate não sustenta 3 peças boas.' },
  { faixa: '3 posts', leitura: 'Piso operacional excepcional.' },
  { faixa: '4 a 7 posts', leitura: 'Faixa normal desejada.' },
  { faixa: '8 a 10 posts', leitura: 'Dia excepcional, com justificativa registrada.' },
  { faixa: 'acima de 10 posts', leitura: 'Bloqueado sem override do dono.' },
] as const;

export function VolumeDePublicacao({ configuration }: { configuration: ConfigurationResponse }) {
  const porChave = new Map(configuration.parameters.map((p) => [p.parameter_key, p]));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section aria-labelledby="volume-diario">
        <Card className="h-full">
          <CabecalhoDePainel
            id="volume-diario"
            texto="Volume diário de publicação"
            para="/controles/tetos-e-limites"
            acao="Alterar"
          />
          <ul className="space-y-1.5 text-sm">
            {VOLUME_LINHAS.map(({ chave, rotulo }) => {
              const parametro = porChave.get(chave);
              const semValor = parametro === undefined || parametro.status !== 'active';
              return (
                <li key={chave} className="flex items-center justify-between gap-2">
                  <span className="text-ink-muted">{rotulo}</span>
                  <span className="shrink-0" data-testid={`volume-${chave}`}>
                    {semValor ? (
                      <span className="text-xs italic text-ink-faint">não configurado</span>
                    ) : (
                      <span className="font-semibold">{String(parametro.value)}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-xs text-ink-faint">
            Quantidade não vira cota cega: o Quality Gate prevalece sobre o piso, e passar do máximo
            desejado exige justificativa registrada.
          </p>
        </Card>
      </section>

      <section aria-labelledby="como-interpreta">
        <Card className="h-full">
          <TituloDePainel id="como-interpreta" texto="Como o sistema interpreta" className="mb-3" />
          <ul className="space-y-2">
            {FAIXAS.map(({ faixa, leitura }) => (
              <li key={faixa} className="rounded-[11px] border border-line bg-surface-sunken p-3">
                <b className="text-sm">{faixa}</b>
                <span className="block text-xs text-ink-muted">{leitura}</span>
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  );
}

export function ContentBoard({ configuration }: { configuration: ConfigurationResponse }) {
  const resource = useResource<ContentListResponse>(() => api.content(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Publicação</h1>
        <p className="mt-2 text-ink-muted">Carregando o portfólio…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Publicação</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar o portfólio.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhuma coluna aparece com zero: zero e &quot;não sabemos&quot; são coisas diferentes.
            Código: {resource.code}
            {resource.correlationId ? ` · correlação: ${resource.correlationId}` : ''}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;
  const agora = NOW();

  // Estado que a esteira produz e que nenhum estágio do quadro cobre seria peça
  // invisível. Melhor uma coluna extra visível do que um item que desaparece.
  const conhecidos = new Set<string>(STAGES.flatMap((s) => s.states));
  const orfaos = data.items.filter((i) => !conhecidos.has(i.state));

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Publicação</h1>
        <p className="mt-1 text-ink-muted">
          {formatNumber(data.total)} peça(s) no portfólio. O estado e o motivo vêm do servidor: esta
          tela não decide o próximo passo, ela mostra onde a peça está.
        </p>
      </header>

      {/*
        O volume diario abre a area, como no desenho entregue: e a pergunta que
        quem opera faz antes de olhar o quadro ("quantas peças cabem hoje?").
      */}
      <VolumeDePublicacao configuration={configuration} />

      {data.total === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma peça no portfólio. Conteúdo nasce de oportunidade registrada, e nenhuma foi aceita
          ainda.
        </p>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-3 xl:grid-cols-4">
          {STAGES.map((stage) => {
            const itens = data.items.filter((i) =>
              (stage.states as readonly string[]).includes(i.state),
            );
            return (
              <section
                key={stage.id}
                className="rounded border border-line p-3"
                aria-labelledby={`stage-${stage.id}`}
              >
                <h2
                  id={`stage-${stage.id}`}
                  className="flex items-baseline justify-between text-xs font-semibold uppercase tracking-wide text-ink-muted"
                >
                  <span>{stage.label}</span>
                  <span>{formatNumber(itens.length)}</span>
                </h2>

                {itens.length === 0 ? (
                  <p className="mt-3 text-xs text-ink-faint">Nenhuma peça neste estágio agora.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {itens.map((item) => (
                      <li key={item.content_id} className="rounded border border-line p-2">
                        <Link
                          to={`/conteudo/${item.content_id}`}
                          className="font-medium underline decoration-dotted"
                        >
                          {item.topic}
                        </Link>
                        <p className="mt-1 text-xs text-ink-muted">
                          {STATE_LABEL[item.state as ContentState] ?? item.state}
                        </p>
                        {item.state_reason ? (
                          <p className="mt-1 text-xs text-ink-faint">{item.state_reason}</p>
                        ) : null}
                        {item.valid_until ? (
                          <p className="mt-1 text-xs text-warn-ink">
                            Validade: {formatInstant(item.valid_until)}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-ink-faint">
                          Atualizado {formatRelative(item.updated_at, agora) ?? 'em data ausente'} ·
                          revisão {formatNumber(item.revision)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {orfaos.length > 0 ? (
        <section className="mt-6 rounded border border-danger-border bg-danger-surface p-4">
          <h2 className="font-medium">
            {formatNumber(orfaos.length)} peça(s) em estado que este quadro não agrupa
          </h2>
          <p className="mt-1 text-xs">
            Isto é um defeito desta tela, não do domínio: um estado novo entrou na esteira sem
            entrar no quadro. As peças aparecem aqui para não ficarem invisíveis.
          </p>
          <ul className="mt-2 space-y-1 text-xs">
            {orfaos.map((item) => (
              <li key={item.content_id}>
                <Link to={`/conteudo/${item.content_id}`} className="underline">
                  {item.topic}
                </Link>{' '}
                · {item.state}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
